/**
 * Shared display and layout config for dashboard stacked-bar charts.
 */

import type { BarChartData } from '@/components/common/stats/HorizontalStackedBarChart';

/** Dashboard IE element ids that gate the chart filter bars. */
export const CHART_FILTER_ELEMENT_IDS = {
  period: 'filter-period',
  dateRange: 'filter-date-range',
  establishment: 'filter-establishment',
  status: 'filter-status',
} as const;

/** Apex series colors keyed by the French session-status labels the API returns. */
export const SESSION_STATUS_SERIES_COLORS: Record<string, string> = {
  Planifiées: 'rgb(61, 107, 168)',
  'En cours': 'rgba(var(--color-error), 0.7)',
  Terminées: 'rgba(var(--color-success), 0.8)',
  Clôturées: 'rgba(var(--accent-purple), 0.65)',
  Annulées: 'rgba(var(--color-error), 0.3)',
};

/** Apex series colors keyed by the French resource labels the API returns. */
export const RESOURCE_SERIES_COLORS: Record<string, string> = {
  Élèves: 'rgb(61, 107, 168)',
  tuteurs: 'rgba(var(--color-success), 0.8)',
  Coordinateurs: 'rgba(var(--accent-purple), 0.65)',
  'VSC Actifs': 'rgba(var(--color-error), 0.7)',
};

/** Filter-chip API keys → series names used in the sessions chart payload. */
export const SESSION_STATUS_KEY_TO_SERIES: Record<string, string> = {
  PLANNED: 'Planifiées',
  IN_PROGRESS: 'En cours',
  COMPLETED: 'Terminées',
  CLOSED: 'Clôturées',
  CANCELLED: 'Annulées',
};

/** Every session-status API key — the default filter-chip selection. */
export const ALL_SESSION_STATUS_KEYS = Object.keys(SESSION_STATUS_KEY_TO_SERIES);

/** French UI copy shared by the dashboard chart widgets. */
export const CHART_LABELS = {
  filter: 'Filtrer',
  sessionsSubtitle: 'par total de séances',
  sessionsEmpty: 'Aucune séance pour ces critères',
  sessionsEmptyHint: 'Modifiez la période ou les filtres de statut',
  resourcesSubtitle: 'par total de ressources',
  resourcesEmpty: 'Aucune ressource pour ces critères',
  resourcesEmptyHint: "Modifiez la période ou le filtre d'établissement",
};

/** Mapped KPI chart payload plus how many bars to size the plot. */
export interface ChartView {
  chartData: BarChartData | null;
  displayCount: number;
}

/** Filter chip owned by the section header; toolbar stays in the chart. */
export interface ChartHeaderFilterProps {
  showFilters: boolean;
  onHasFiltersChange: (hasFilters: boolean) => void;
}

/** Which dashboard IE filters a chart toolbar should show. */
export interface ChartFilterVisibility {
  showPeriodFilter: boolean;
  showDateRangeFilter: boolean;
  showEstablishmentFilter: boolean;
  showStatusFilter: boolean;
  hasAnyFilter: boolean;
}

/** Fallback height for the section loading skeleton (no bar count yet). */
export const CHART_SKELETON_HEIGHT = 320;
